import type { ReactNode } from 'react';
import { MapPin, Save, X } from 'lucide-react';
import { Button, Stack } from '../../atoms';
import { StatusBlock } from '../../molecules';
import {
	DetailField as Field,
	DetailGrid2 as Grid2,
	DetailSection as Section,
	DetailSelectInput as SelectInput,
	DetailTextInput as TextInput,
	DetailToggle as Toggle,
} from '../../molecules/detail-fields/detail-fields';
import { PageContainer, PageHeader } from '../../templates';

export interface ConfigurationDetailsTotals {
	kwp: number;
	battery: number;
	ore: number | null;
	productionMwh: number | null;
	cons: number | null;
	consResPct: number | null;
	podProd: number;
	podCons: number;
	soci: number;
	sociRes: number;
	missingProfiles: number;
	missingStorage: number;
	/**
	 * Quota % di kWp degli impianti in esercizio prima del 16/12/2021. La
	 * ricava il database dalle date di esercizio: si mostra, non si modifica
	 * (INB-328).
	 */
	preEsistentePct?: number | null;
}
export interface ConfigurationDetailsViewProps {
	nome: string;
	form: Record<string, string>;
	totals: ConfigurationDetailsTotals | null;
	loading: boolean;
	loadError: boolean;
	/**
	 * L'errore è un 403 o un 404: la configurazione non è accessibile, non un
	 * guasto temporaneo da riprovare (INB-352).
	 */
	loadErrorKind?: 'forbidden' | 'notFound';
	saving: boolean;
	geocoding: boolean;
	disabled: boolean;
	demo: boolean;
	errors: Record<string, string>;
	mqttSection?: ReactNode;
	/**
	 * La zona del FCzonale che le RO assegnano alla regione del comune, quando il
	 * comune è stato geocodificato (INB-326): se la zona scelta è un'altra, il
	 * campo lo segnala. La scelta resta libera, come override.
	 */
	zonaDaComune?: { zona: 'nord' | 'centro' | 'sud'; regione: string } | null;
	onFieldChange: (key: string, value: string) => void;
	onDisabledChange: (value: boolean) => void;
	onDemoChange: (value: boolean) => void;
	onSave: () => void;
	onCancel: () => void;
	onGeocode: () => void;
}
const ROMA = { lat: '41.9', lon: '12.49' };
const ZONA_OPTIONS = [
	{ value: 'nord', label: 'Nord' },
	{ value: 'centro', label: 'Centro' },
	{ value: 'sud', label: 'Sud' },
];
function SummaryValue({
	label,
	value,
	unit,
}: {
	label: string;
	value: number | null | undefined;
	unit?: string;
}) {
	return (
		<div>
			<dt className="text-sm text-text-muted">{label}</dt>
			<dd className="mt-1 font-mono text-base font-semibold text-text">
				{value == null
					? 'Non disponibile'
					: `${value.toLocaleString('it-IT', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`}
			</dd>
		</div>
	);
}
export function ConfigurationDetailsView({
	nome,
	form,
	totals,
	loading,
	loadError,
	loadErrorKind,
	saving,
	geocoding,
	disabled,
	demo,
	errors,
	mqttSection,
	zonaDaComune = null,
	onFieldChange,
	onDisabledChange,
	onDemoChange,
	onSave,
	onCancel,
	onGeocode,
}: ConfigurationDetailsViewProps) {
	const v = (key: string) => form[key] ?? '';
	if (loading) {
		return <StatusBlock kind="loading" size="lg" message="Caricamento..." />;
	}

	if (loadError)
		return (
			<StatusBlock
				kind="error"
				message={
					loadErrorKind === 'forbidden'
						? 'Non hai accesso a questa configurazione: appartiene a una CER che non amministri.'
						: loadErrorKind === 'notFound'
							? 'Questa configurazione non esiste o è stata eliminata.'
							: 'Impossibile caricare la configurazione. Riprova ricaricando la pagina.'
				}
			/>
		);
	return (
		<PageContainer width="narrow">
			<Stack gap="xl">
				<PageHeader
					size="medium"
					title={nome || 'Configurazione'}
					actions={
						<>
							<Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
								<X />
								Annulla
							</Button>
							<Button type="button" variant="accent" onClick={onSave} disabled={saving}>
								<Save />
								{saving ? 'Salvataggio...' : 'Salva'}
							</Button>
						</>
					}
				/>

				<Section title="Stato">
					<Toggle
						label="Configurazione disabilitata"
						hint="Nascosta a soci e referenti: sparisce da switcher e viste dati finché non viene riabilitata"
						checked={disabled}
						onChange={() => onDisabledChange(!disabled)}
					/>
					<Toggle
						label="Modalità demo"
						hint="Abilita la generazione di dati dimostrativi (curve GSE, bollette, letture e stime) per i misuratori di questa configurazione"
						checked={demo}
						onChange={() => onDemoChange(!demo)}
					/>
				</Section>

				<Section title="Cabina Primaria & GSE" hint="Art. 3 D.M. 414/2023">
					<Field label="Codice Cabina Primaria (GSE/Terna)">
						<TextInput
							value={v('cabinaPrimariaCodice')}
							onChange={(x) => onFieldChange('cabinaPrimariaCodice', x)}
							placeholder="es. CP_NORD_001"
						/>
					</Field>
					<Field label="Area Convenzionale (identificativo GSE)">
						<TextInput
							value={v('areaConvenzionale')}
							onChange={(x) => onFieldChange('areaConvenzionale', x)}
						/>
					</Field>
					<Grid2>
						<Field
							label="Zona Geografica"
							hint="Macro-zona GSE della configurazione."
							warning={
								!v('zonaGeografica')
									? 'Zona non impostata: finché non la scegli vale Sud. Sceglila dalla regione, oppure scrivi il comune sede e usa «Ricava dal comune sede» qui sotto.'
									: zonaDaComune && v('zonaGeografica') !== zonaDaComune.zona
										? `${zonaDaComune.regione} è in zona ${ZONA_OPTIONS.find((o) => o.value === zonaDaComune.zona)?.label ?? zonaDaComune.zona} per le Regole Operative GSE: la zona scelta non corrisponde.`
										: undefined
							}
							suggestion={
								zonaDaComune && v('zonaGeografica') !== zonaDaComune.zona
									? {
											label: `Usa ${ZONA_OPTIONS.find((o) => o.value === zonaDaComune.zona)?.label ?? zonaDaComune.zona} (${zonaDaComune.regione})`,
											onApply: () => onFieldChange('zonaGeografica', zonaDaComune.zona),
										}
									: undefined
							}
						>
							<SelectInput
								value={v('zonaGeografica')}
								onChange={(x) => onFieldChange('zonaGeografica', x)}
								options={ZONA_OPTIONS}
								placeholder="- seleziona zona -"
							/>
						</Field>
						<Field label="Comune sede">
							<TextInput value={v('comune')} onChange={(x) => onFieldChange('comune', x)} />
						</Field>
						<Field label="Codice GSE">
							<TextInput value={v('codiceGSE')} onChange={(x) => onFieldChange('codiceGSE', x)} />
						</Field>
						<Field label="Data Attivazione GSE (SPC)">
							<TextInput
								type="date"
								value={v('dataAttivazioneGSE')}
								onChange={(x) => onFieldChange('dataAttivazioneGSE', x)}
							/>
						</Field>
					</Grid2>
				</Section>

				<Section
					title="Posizione per i dati meteo"
					hint="Obbligatoria. È la località di cui si scarica l'irraggiamento, e quella su cui la produzione stimata viene calibrata: sbagliata, le previsioni seguono il tempo di un'altra parte d'Italia."
				>
					<Grid2>
						<Field
							label="Latitudine"
							hint={`Gradi decimali. Default Roma: ${ROMA.lat}`}
							error={errors.lat}
						>
							<TextInput
								value={v('lat')}
								onChange={(x) => onFieldChange('lat', x)}
								placeholder={ROMA.lat}
							/>
						</Field>
						<Field
							label="Longitudine"
							hint={`Gradi decimali. Default Roma: ${ROMA.lon}`}
							error={errors.lon}
						>
							<TextInput
								value={v('lon')}
								onChange={(x) => onFieldChange('lon', x)}
								placeholder={ROMA.lon}
							/>
						</Field>
					</Grid2>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={onGeocode}
						disabled={geocoding || saving}
					>
						<MapPin />
						{geocoding ? 'Ricerca…' : 'Ricava dal comune sede'}
					</Button>
				</Section>

				<Section
					title="Impianti di produzione"
					hint="Totali calcolati dagli impianti e dagli accumuli collegati ai POD della configurazione."
				>
					<dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
						<SummaryValue label="Potenza installata" value={totals?.kwp} unit="kWp" />
						<SummaryValue
							label="Storage installato"
							value={totals?.missingStorage ? null : totals?.battery}
							unit="kWh"
						/>
						<SummaryValue
							label="Produzione annua tipica"
							value={totals?.productionMwh}
							unit="MWh"
						/>
						<SummaryValue label="Ore equivalenti / anno" value={totals?.ore} unit="h" />
					</dl>
					<p className="text-sm text-text-muted">
						Le ore equivalenti sono il rapporto tra produzione annua tipica e potenza installata.
					</p>
					{!!totals?.missingStorage && (
						<p className="text-sm text-text-muted">
							Capacità non compilata per {totals.missingStorage} punti di accumulo.
						</p>
					)}
				</Section>

				<Section
					title="Consumi e POD"
					hint="POD distinti e soci non receduti. Un POD prosumer è contato sia in produzione sia in consumo."
				>
					<dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
						<SummaryValue label="Consumo annuo tipico" value={totals?.cons} unit="MWh" />
						<SummaryValue
							label="Quota dei consumi residenziali"
							value={totals?.consResPct}
							unit="%"
						/>
						<SummaryValue label="POD produzione" value={totals?.podProd} />
						<SummaryValue label="POD consumo" value={totals?.podCons} />
						<SummaryValue label="Numero soci" value={totals?.soci} />
						<SummaryValue label="Soci residenziali" value={totals?.sociRes} />
					</dl>
					<p className="text-sm text-text-muted">
						I totali annui sommano i profili tipici dei punti di misura; non rappresentano consumi o
						produzione misurati.
					</p>
					{!!totals?.missingProfiles && (
						<output className="block text-sm text-text-muted">
							Totali energetici incompleti: mancano i profili di {totals.missingProfiles} punti di
							misura.
						</output>
					)}
				</Section>

				{mqttSection}

				<Section title="Soglia dell'energia incentivabile">
					<Grid2>
						<Field
							label="Contributo in conto capitale, es. PNRR (%)"
							hint="Il contributo PNRR è del singolo impianto e si imposta nella scheda del socio produttore. Questo valore vale solo per gli impianti che non ne hanno uno proprio."
						>
							<TextInput
								type="number"
								value={v('pnrrGrantPct')}
								onChange={(x) => onFieldChange('pnrrGrantPct', x)}
							/>
						</Field>
						{/* Non più un campo: la quota si ricava dalle date di esercizio degli
						    impianti, che sono l'unica fonte (INB-328). */}
						<Field
							label="% Impianti pre-esistenti"
							hint="Calcolata dalle date di esercizio degli impianti (prima del 16/12/2021). Limite 30%."
							warning={
								(totals?.preEsistentePct ?? 0) > 30
									? 'Oltre il 30%: la configurazione non rispetta il limite degli impianti esistenti (RO §1.2.1.2).'
									: undefined
							}
						>
							<TextInput
								type="text"
								value={`${Math.round((totals?.preEsistentePct ?? 0) * 10) / 10}%`}
								onChange={() => {}}
								disabled
							/>
						</Field>
					</Grid2>
				</Section>
			</Stack>
		</PageContainer>
	);
}
